<script setup lang="ts">
import { VNodeRenderer } from '@layouts/components/VNodeRenderer'
import { themeConfig } from '@themeConfig'
import Cookies from 'js-cookie'
import { VForm } from 'vuetify/components/VForm'

import { transformLogo } from '@/@layouts/utils'
import { EndpointsEnum } from '@/assets/global/enums/endpoints/EndpointsEnum'
import miscUnderMaintenance from '@images/pages/misc-under-maintenance.png'
import logo from '@images/svg/logo.svg?raw'

definePage({
  meta: {
    layout: 'blank',
    unauthenticatedOnly: true,
  },
})

document.title = `${themeConfig.app.title} | Itinerários Nacionais`

useAbility()

const refVForm = ref<VForm>()
const progressCircular = ref()

const openedPanels = ref<number[]>([0])
const openedAlert = ref<boolean>(!!useCookie('error').value)
const error = ref<string>(String(useCookie('error').value))
const urlSuporte = `https://${import.meta.env.VITE_SERVER_AUTH_URL}/suporte`

Cookies.remove('error', { domain: `${import.meta.env.VITE_DOMAIN_BASE}` })

const onSubmit = () => {
  openedAlert.value = false
  progressCircular.value = true
  window.location.href = EndpointsEnum.LOGIN.replace('{apiBaseUrl}', import.meta.env.VITE_API_BASE_URL)
}

const itemsFaq = [
  {
    title: 'Por que usar minha conta SENAI?',
    text: 'É um serviço que fornece um login único para todas as plataformas digitais do SENAI. Com apenas uma conta e senha, você pode acessar o Itinerários Nacionais, o Meu SENAI, o Portal da Indústria e diversos outros serviços com praticidade e segurança.',
  },
  {
    title: 'O que são os itinerários nacionais?',
    text: 'São percursos formativos pré-definidos pelo SENAI para atender às demandas específicas de cada área tecnológica. Eles combinam cursos de diferentes níveis e setores da economia, permitindo personalizar formações conforme os objetivos profissionais.',
  },
  {
    title: 'Qual a metodologia utilizada na construção do itinerário?',
    text: 'A Metodologia SENAI de Educação Profissional (MSEP) está centrada no desenvolvimento de competências. O SENAI estrutura o currículo com base na análise do perfil profissional. Esta análise identifica as capacidades básicas, técnicas e socioemocionais pertinentes à ocupação, que serão incorporadas ao conteúdo formativo das unidades curriculares da formação.',
  },
]
</script>

<template>
  <VRow
    no-gutters
    class="auth-wrapper bg-surface"
  >
    <VCol
      cols="12"
      lg="5"
      class="auth-card-v2 d-flex align-center justify-center"
    >
      <VCard
        flat
        :max-width="500"
        class="mt-12 mt-sm-0 pd-4"
        width="100%"
      >
        <VCardText>
          <VNodeRenderer
            :nodes="h('div', { innerHTML: transformLogo(logo) })"
            class="login-logo"
          />
        </VCardText>

        <VCardText>
          <VForm
            ref="refVForm"
            @submit.prevent="onSubmit"
          >
            <VRow>
              <VCol cols="12">
                <VBtn
                  block
                  type="submit"
                >
                  <span>Entrar com conta <strong>SENAI</strong></span>
                </VBtn>
              </VCol>
            </VRow>
          </VForm>
        </VCardText>

        <div style="height: 120px; color: blue">
          <VCardText
            v-if="openedAlert"
            class="pt-0"
          >
            <VAlert
              color="error"
              variant="tonal"
            >
              <div>
                <div v-if="error === 'user_not_found'">
                  Houve um problema ao autenticar sua conta SENAI, <br>
                  <a
                    :href="urlSuporte"
                    class="font-weight-medium"
                    :style="{ color: 'red' }"
                  >contate o suporte!</a>
                </div>
                <div v-else-if="error === 'user_forbidden'">
                  Seu usuário está sem acesso ao Itinerários Nacionais
                </div>
                <div v-else-if="error === 'sign_out_unsuccessfully'">
                  Algo de errado aconteceu ao tentar sair do sistema.
                </div>
                <div v-else>
                  Ops! Algumas ações talvez não funcionem.
                </div>
              </div>
            </VAlert>
          </VCardText>

          <VCardText v-if="progressCircular">
            <VRow class="justify-center">
              <VCol
                cols="12"
                class="text-center"
              >
                <VProgressCircular
                  :size="52"
                  :rotate="500"
                  color="primary"
                  indeterminate
                />
              </VCol>
            </VRow>
          </VCardText>
        </div>
      </VCard>
    </VCol>

    <VCol
      lg="7"
      class="d-none d-lg-flex"
    >
      <div class="position-relative bg-background rounded-lg w-100 me-8 my-8">
        <div class="misc-wrapper pa-10">
          <div class="text-center mt-12 mb-2">
            <h4 class="text-h4 font-weight-medium mb-3">
              Boas-vindas ao <span class="text-capitalize"> {{ themeConfig.app.title }} </span>
            </h4>
            <p>Deixe-nos ajudar a responder às perguntas mais comuns.</p>
          </div>

          <div class="misc-avatar w-100 text-center">
            <VImg
              :src="miscUnderMaintenance"
              :max-width="300"
              class="mx-auto pt-10 pb-6"
            />
          </div>

          <div class="w-auto mx-10 mb-10">
            <VExpansionPanels
              v-model="openedPanels"
              variant="accordion"
            >
              <VExpansionPanel
                v-for="item in itemsFaq"
                :key="item"
              >
                <VExpansionPanelTitle>
                  {{ item.title }}
                </VExpansionPanelTitle>
                <VExpansionPanelText>
                  {{ item.text }}
                </VExpansionPanelText>
              </VExpansionPanel>
            </VExpansionPanels>
          </div>
        </div>
      </div>
    </VCol>
  </VRow>
</template>

<style lang="scss">
@use "@core/scss/template/pages/page-auth.scss";

.login-logo svg {
  width: 100%;
  height: auto;
}
</style>

<style scoped>
a {
  color: red;
}
</style>
